import { useCallback, useEffect, useMemo, useState } from "react";
import { Send, Loader2, Mail, Sparkles, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { adminReplyTemplates, adminSendReply, adminListReplies } from "@/lib/api";
import ReplyHistoryList from "@/components/site/ReplyHistoryList";

function renderTemplateText(text, contact, calendlyUrl) {
  if (!text) return "";
  const name = (contact?.name || "").trim();
  const firstName = name ? name.split(" ")[0] : "there";
  return text
    .replaceAll("{{first_name}}", firstName)
    .replaceAll("{{name}}", name || "there")
    .replaceAll("{{calendly_url}}", calendlyUrl || "")
    .replaceAll("{{phone}}", "708-794-8564")
    .replaceAll("{{email}}", "contact@trjsvc.com");
}

export default function ReplyDialog({ open, onOpenChange, contact, onSent }) {
  const [templates, setTemplates] = useState([]);
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Memoised so the effect dep array stays stable
  const applyTemplate = useCallback((tpl, calendly) => {
    if (!tpl) return;
    setTemplateId(tpl.id);
    setSubject(renderTemplateText(tpl.subject, contact, calendly));
    setBody(renderTemplateText(tpl.body, contact, calendly));
  }, [contact]);

  useEffect(() => {
    if (!open || !contact) return;
    let active = true;
    adminReplyTemplates()
      .then((data) => {
        if (!active) return;
        setTemplates(data.templates || []);
        setCalendlyUrl(data.calendly_url || "");
        applyTemplate((data.templates || [])[0], data.calendly_url);
      })
      .catch(() => toast.error("Failed to load templates."));
    adminListReplies(contact.id)
      .then((d) => active && setHistory(d.items || []))
      .catch(() => {});
    return () => { active = false; };
  }, [open, contact, applyTemplate]);

  const onPickTemplate = useCallback((id) => {
    const tpl = templates.find((x) => x.id === id);
    applyTemplate(tpl, calendlyUrl);
  }, [templates, calendlyUrl, applyTemplate]);

  const onSend = useCallback(async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    setSending(true);
    try {
      const res = await adminSendReply(contact.id, { templateId, subject, body });
      toast.success("Reply sent.", { description: `To: ${res.to}` });
      onSent && onSent(res);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Send failed.", { description: typeof detail === "string" ? detail : "Please try again." });
    } finally {
      setSending(false);
    }
  }, [contact, templateId, subject, body, onSent, onOpenChange]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId],
  );

  const toggleHistory = useCallback(() => setShowHistory((s) => !s), []);

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="reply-dialog"
        className="sm:max-w-2xl rounded-none border-t-4 border-[var(--trj-gold)] p-0 max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="bg-[var(--trj-navy)] text-white px-7 py-5 relative shrink-0">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 text-white/60 hover:text-white"
            data-testid="reply-dialog-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="trj-eyebrow !text-[var(--trj-gold)] mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Quick Reply
          </div>
          <DialogHeader className="p-0">
            <DialogTitle className="font-serif-trj text-2xl text-white leading-tight">
              Reply to {contact.name}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm">
              {contact.email}{contact.phone ? ` · ${contact.phone}` : ""}
              {contact.service_interest ? ` · ${contact.service_interest}` : ""}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={onSend} className="px-7 py-5 bg-white overflow-y-auto" data-testid="reply-form">
          {/* Original message excerpt */}
          {contact.message && (
            <div className="mb-5 bg-[var(--trj-light)] border-l-4 border-[var(--trj-navy)]/20 px-4 py-3">
              <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)] mb-1">Original message</div>
              <div className="text-[13px] text-[var(--trj-navy)] whitespace-pre-wrap line-clamp-4">{contact.message}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Template</Label>
              <Select value={templateId} onValueChange={onPickTemplate}>
                <SelectTrigger className="rounded-none mt-1" data-testid="reply-template-select">
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="rounded-none" data-testid={`reply-template-option-${t.id}`}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTemplate && (
                <p className="text-[11px] text-[var(--trj-muted)] mt-1.5">
                  Variables: <code className="bg-[var(--trj-light)] px-1">{"{{first_name}}"}</code>, <code className="bg-[var(--trj-light)] px-1">{"{{calendly_url}}"}</code> are auto-substituted.
                </p>
              )}
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="rounded-none mt-1"
                required
                data-testid="reply-subject"
                placeholder="Reply subject"
              />
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-widest text-[var(--trj-muted)]">Message</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={11}
                className="rounded-none mt-1 font-body-trj text-[13.5px] leading-relaxed"
                required
                data-testid="reply-body"
                placeholder="Type your message…"
              />
            </div>

            <ReplyHistoryList
              history={history}
              showHistory={showHistory}
              onToggle={toggleHistory}
            />
          </div>
        </form>

        <div className="px-7 py-4 bg-[var(--trj-light)] border-t border-gray-200 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 text-[11px] text-[var(--trj-muted)]">
            <Mail className="w-3.5 h-3.5" /> Sending as <strong className="text-[var(--trj-navy)] ml-1">TRJ Services</strong> · Replies go to your inbox
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="trj-btn-outline-navy text-sm"
              data-testid="reply-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={onSend}
              disabled={sending}
              className="trj-btn-green text-sm disabled:opacity-60"
              data-testid="reply-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : "Send Reply"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
